"use client"

import { useCallback, useEffect, useRef } from "react"

export function useUnsavedChangesWarning(isDirty: boolean, message = "Changes you made may not be saved.") {
  const dirtyRef = useRef(isDirty)
  const messageRef = useRef(message)

  useEffect(() => {
    dirtyRef.current = isDirty
  }, [isDirty])

  useEffect(() => {
    messageRef.current = message
  }, [message])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return
      event.preventDefault()
      // Use empty string to trigger the browser's native message
      event.returnValue = ""
      return ""
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!dirtyRef.current) return
      const target = event.target as Element | null
      const anchor = target && (target.closest?.("a[href]") as HTMLAnchorElement | null)
      if (!anchor) return

      if (
        anchor.target === "_blank" ||
        anchor.hasAttribute("download") ||
        (event as MouseEvent).metaKey ||
        (event as MouseEvent).ctrlKey ||
        (event as MouseEvent).shiftKey ||
        (event as MouseEvent).altKey
      ) {
        return
      }

      const href = anchor.getAttribute("href")
      if (!href) return
      const url = new URL(href, window.location.origin)

      if (url.origin !== window.location.origin) return

      const ok = window.confirm(messageRef.current)
      if (!ok) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    document.addEventListener("click", onClick, true)
    return () => {
      document.removeEventListener("click", onClick, true)
    }
  }, [])

  useEffect(() => {
    const onPopState = () => {
      if (!dirtyRef.current) return
      const ok = window.confirm(messageRef.current)
      if (!ok) {
        history.pushState(null, "", window.location.href)
      }
    }
    window.addEventListener("popstate", onPopState)
    return () => {
      window.removeEventListener("popstate", onPopState)
    }
  }, [])

  const bypass = useCallback(() => {}, [])

  return bypass
}
